import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BadgeComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  FormCheckComponent,
  FormCheckInputDirective,
  FormCheckLabelDirective,
  FormControlDirective,
  FormLabelDirective,
  FormSelectDirective,
  RowComponent,
  SpinnerComponent,
  TabDirective,
  TableDirective,
  TabsComponent,
  TabsListComponent
} from '@coreui/angular-pro';
import { ChartjsComponent } from '@coreui/angular-chartjs';
import { ChartData, ChartOptions } from 'chart.js';
import { TranslatePipe, TranslateService } from 'i18n';
import { Logger } from 'shared';
import {
  CardPartnerSeriesPoint,
  AdminCardPartnerService,
  CardPartnerOverview,
  CardPartnerRankingQuery,
  CardPartnerRankingRow
} from '../../../services/admin-card-partner.service';
import {
  CARD_PARTNER_KYC_STATUSES,
  cardPartnerCardColor,
  cardPartnerChainLabel,
  cardPartnerCrypto,
  cardPartnerKeyOr,
  cardPartnerKycColor,
  cardPartnerMoney,
  cardPartnerStatusColor
} from '../../../utils/card-partner-format';

type SortKey = 'spend' | 'fees' | 'tx_count' | 'deposits' | 'withdrawals' | 'activated_at' | 'kyc' | 'last_tx';

/** `YYYY-MM` of a UTC date — the backend buckets months in UTC. */
function monthKey(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

function shiftMonth(month: string, delta: number): string {
  const [y, m] = month.split('-').map(Number);
  return monthKey(new Date(Date.UTC(y, m - 1 + delta, 1)));
}

/**
 * Admin analytics for the card programme: a monthly summary (KPIs, breakdowns,
 * 12-month chart) and a ranking of cardholders. Everything is read from our
 * mirror of the card partner's data; the partner is only ever called "card
 * partner" on screen (customer requirement — the provider's name is not shown).
 *
 * Month is kept in the URL (`?month=YYYY-MM|all`) so the detail page and the
 * browser's back button land on the same period.
 */
@Component({
  selector: 'app-admin-card-partner-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ContainerComponent,
    RowComponent,
    ColComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    TableDirective,
    BadgeComponent,
    ButtonDirective,
    SpinnerComponent,
    FormControlDirective,
    FormLabelDirective,
    FormSelectDirective,
    FormCheckComponent,
    FormCheckInputDirective,
    FormCheckLabelDirective,
    ChartjsComponent,
    TabsComponent,
    TabsListComponent,
    TabDirective,
    TranslatePipe
  ],
  templateUrl: './admin-card-partner-page.component.html',
  styleUrls: ['./admin-card-partner-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminCardPartnerPageComponent {
  private logger = new Logger('AdminCardPartnerPageComponent');
  private service = inject(AdminCardPartnerService);
  private translate = inject(TranslateService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  readonly currentMonth = monthKey(new Date());
  readonly kycStatuses = CARD_PARTNER_KYC_STATUSES;
  readonly perPageOptions = [25, 50, 100, 200];

  month = signal<string>(this.initialMonth());
  isAllTime = computed(() => this.month() === 'all');

  // ── summary ─────────────────────────────────────────────────────────────
  overview = signal<CardPartnerOverview | null>(null);
  overviewLoading = signal(false);
  overviewError = signal(false);

  // ── ranking ─────────────────────────────────────────────────────────────
  rows = signal<CardPartnerRankingRow[]>([]);
  rankingLoading = signal(false);
  total = signal(0);
  totalPages = signal(0);
  page = signal(1);
  perPage = signal(25);
  sort = signal<SortKey>('spend');
  dir = signal<'asc' | 'desc'>('desc');
  search = signal('');
  kycFilter = signal('');
  hasCardFilter = signal<'' | 'yes' | 'no'>('');
  linkedFilter = signal<'' | 'yes' | 'no'>('');
  activeOnly = signal(false);
  exporting = signal(false);

  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  kycRows = computed(() => this.countRows(this.overview()?.accounts.by_kyc_status, 'kyc'));
  accountStatusRows = computed(() => this.countRows(this.overview()?.accounts.by_account_status, 'account'));
  cardStatusRows = computed(() => this.countRows(this.overview()?.cards.by_card_status, 'card'));
  physicalRows = computed(() => {
    const src = this.overview()?.cards.by_physical_status ?? {};
    return Object.entries(src)
      .map(([value, count]) => ({
        value,
        label: value ? cardPartnerKeyOr('physical', value) : 'mycards.partner.physical_none',
        count
      }))
      .sort((a, b) => b.count - a.count);
  });

  depositsUsdt = computed(() => this.tokenVolume(this.overview()?.deposits.settled_by_token, 'USDT'));
  depositsUsdc = computed(() => this.tokenVolume(this.overview()?.deposits.settled_by_token, 'USDC'));

  /** Which chart tab is open: 12 months or 30 days. Only the labels differ. */
  chartTab = signal<'months' | 'days'>('months');

  chartData = computed<ChartData | null>(() => this.buildChartData(this.overview()?.series, p => p.month ?? ''));
  dailyChartData = computed<ChartData | null>(() =>
    // Day labels without the year — 30 of them have to fit on one axis.
    this.buildChartData(this.overview()?.daily_series, p => (p.day ?? '').slice(5)));

  private buildChartData(series: CardPartnerSeriesPoint[] | undefined, label: (p: CardPartnerSeriesPoint) => string): ChartData | null {
    if (!series?.length) return null;
    const t = (k: string) => this.translate.translate(`card_partner_admin.${k}`);
    return {
      labels: series.map(label),
      datasets: [
        { type: 'bar', label: t('kpi_spend'), data: series.map(p => Number(p.spend)), backgroundColor: 'rgba(0, 229, 255, 0.7)', yAxisID: 'y' },
        { type: 'bar', label: t('kpi_deposits'), data: series.map(p => Number(p.deposits)), backgroundColor: 'rgba(0, 255, 159, 0.55)', yAxisID: 'y' },
        { type: 'bar', label: t('kpi_fees'), data: series.map(p => Number(p.fees)), backgroundColor: 'rgba(255, 193, 7, 0.8)', yAxisID: 'y' },
        { type: 'line', label: t('kpi_activations'), data: series.map(p => p.activations), borderColor: '#e55353', backgroundColor: '#e55353', tension: 0.3, yAxisID: 'y1' },
        { type: 'line', label: t('kpi_new_accounts'), data: series.map(p => p.new_accounts), borderColor: '#9da5b1', backgroundColor: '#9da5b1', borderDash: [5, 5], tension: 0.3, yAxisID: 'y1' }
      ]
    } as ChartData;
  }

  chartOptions: ChartOptions = this.buildChartOptions();

  constructor() {
    // Summary follows the month.
    effect(() => {
      const month = this.month();
      untracked(() => this.loadOverview(month));
    });

    // Ranking follows month, paging, sorting and filters.
    effect(() => {
      const query = this.rankingQuery();
      untracked(() => this.loadRanking(query));
    });
  }

  // ── month picker ────────────────────────────────────────────────────────

  setMonth(value: string): void {
    const month = value === 'all' || /^\d{4}-\d{2}$/.test(value) ? value : this.currentMonth;
    if (month === this.month()) return;
    this.month.set(month);
    this.page.set(1);
    this.router.navigate([], { relativeTo: this.route, queryParams: { month }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  prevMonth(): void {
    this.setMonth(shiftMonth(this.isAllTime() ? this.currentMonth : this.month(), this.isAllTime() ? 0 : -1));
  }

  nextMonth(): void {
    if (this.isAllTime()) {
      this.setMonth(this.currentMonth);
      return;
    }
    const next = shiftMonth(this.month(), 1);
    if (next <= this.currentMonth) this.setMonth(next);
  }

  canGoNext(): boolean {
    return this.isAllTime() || this.month() < this.currentMonth;
  }

  // ── ranking controls ────────────────────────────────────────────────────

  sortBy(key: SortKey): void {
    if (this.sort() === key) {
      this.dir.set(this.dir() === 'desc' ? 'asc' : 'desc');
    } else {
      this.sort.set(key);
      // Text-like columns read naturally ascending; amounts and dates descending.
      this.dir.set(key === 'kyc' ? 'asc' : 'desc');
    }
    this.page.set(1);
  }

  sortIcon(key: SortKey): string {
    if (this.sort() !== key) return '';
    return this.dir() === 'desc' ? '▼' : '▲';
  }

  onSearch(value: string): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.search.set(value.trim());
      this.page.set(1);
    }, 300);
  }

  setFilter(filter: 'kyc' | 'card' | 'linked', value: string): void {
    if (filter === 'kyc') this.kycFilter.set(value);
    if (filter === 'card') this.hasCardFilter.set(value as '' | 'yes' | 'no');
    if (filter === 'linked') this.linkedFilter.set(value as '' | 'yes' | 'no');
    this.page.set(1);
  }

  setActiveOnly(value: boolean): void {
    this.activeOnly.set(value);
    this.page.set(1);
  }

  setPerPage(value: number | string): void {
    this.perPage.set(Number(value));
    this.page.set(1);
  }

  goToPage(page: number): void {
    if (page < 1 || page > this.totalPages()) return;
    this.page.set(page);
  }

  openDetail(row: CardPartnerRankingRow): void {
    this.router.navigate(['/', this.lang(), 'admin', 'card-partner', row.partner_user_id], { queryParams: { month: this.month() } });
  }

  userLink(row: CardPartnerRankingRow): string[] | null {
    return row.user ? ['/', this.lang(), 'admin', 'users', String(row.user.id)] : null;
  }

  openUser(event: Event, row: CardPartnerRankingRow): void {
    event.stopPropagation();
    const link = this.userLink(row);
    if (link) this.router.navigate(link);
  }

  /**
   * Downloaded through HttpClient rather than a plain link: the auth token is
   * attached by the HTTP interceptor, which a browser navigation would not carry.
   */
  exportCsv(): void {
    if (this.exporting()) return;
    this.exporting.set(true);
    this.service.exportUsers(this.rankingQuery())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: csv => {
          this.exporting.set(false);
          const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
          const link = document.createElement('a');
          link.href = url;
          link.download = `card-partner-users-${this.month()}.csv`;
          link.click();
          URL.revokeObjectURL(url);
        },
        error: err => {
          this.exporting.set(false);
          this.logger.error('Failed to export card partner ranking', err);
        }
      });
  }

  // ── display helpers ─────────────────────────────────────────────────────

  keyOr = cardPartnerKeyOr;
  kycColor = cardPartnerKycColor;
  cardColor = cardPartnerCardColor;
  statusColor = cardPartnerStatusColor;
  chainLabel = cardPartnerChainLabel;

  money(value: string | number | null | undefined): string {
    return cardPartnerMoney(value, this.lang());
  }

  crypto(value: string | null | undefined): string {
    return cardPartnerCrypto(value, this.lang());
  }

  date(value: string | null | undefined): string {
    return value ? new Date(value).toLocaleDateString(this.lang()) : '—';
  }

  dateTime(value: string | null | undefined): string {
    return value ? new Date(value).toLocaleString(this.lang(), { dateStyle: 'short', timeStyle: 'short' }) : '—';
  }

  isZero(value: string | null | undefined): boolean {
    return !Number(value ?? 0);
  }

  // ── loading ─────────────────────────────────────────────────────────────

  private rankingQuery(): CardPartnerRankingQuery {
    return {
      month: this.month(),
      sort: this.sort(),
      dir: this.dir(),
      page: this.page(),
      per_page: this.perPage(),
      q: this.search() || undefined,
      kyc_status: this.kycFilter() || undefined,
      has_card: this.hasCardFilter(),
      linked: this.linkedFilter(),
      active: this.activeOnly()
    };
  }

  private loadOverview(month: string): void {
    this.overviewLoading.set(true);
    this.overviewError.set(false);
    this.service.overview(month)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.overview.set(data);
          this.overviewLoading.set(false);
        },
        error: err => {
          this.logger.error('Failed to load card partner overview', err);
          this.overviewError.set(true);
          this.overviewLoading.set(false);
        }
      });
  }

  private loadRanking(query: CardPartnerRankingQuery): void {
    this.rankingLoading.set(true);
    this.service.users(query)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: res => {
          // A slow response for an older query must not overwrite a newer one.
          if (JSON.stringify(query) !== JSON.stringify(this.rankingQuery())) return;
          this.rows.set(res.items);
          this.total.set(res.total);
          this.totalPages.set(res.total_pages);
          this.rankingLoading.set(false);
        },
        error: err => {
          this.logger.error('Failed to load card partner ranking', err);
          this.rankingLoading.set(false);
        }
      });
  }

  private initialMonth(): string {
    const fromUrl = this.route.snapshot.queryParamMap.get('month');
    return fromUrl && (fromUrl === 'all' || /^\d{4}-\d{2}$/.test(fromUrl)) ? fromUrl : this.currentMonth;
  }

  private countRows(src: Record<string, number> | undefined, kind: string): { value: string; label: string; count: number }[] {
    return Object.entries(src ?? {})
      .map(([value, count]) => ({ value, label: value ? cardPartnerKeyOr(kind, value) : '—', count }))
      .sort((a, b) => b.count - a.count);
  }

  private tokenVolume(rows: { currency: string | null; volume: string }[] | undefined, token: string): string {
    return rows?.find(r => r.currency === token)?.volume ?? '0';
  }

  private lang(): string {
    try {
      return this.translate.getActiveLang() || localStorage.getItem('language') || 'en';
    } catch {
      return 'en';
    }
  }

  /** Tick/legend colour from the theme so the chart reads on light and dark boards. */
  private buildChartOptions(): ChartOptions {
    let color = '#9da5b1';
    try {
      color = getComputedStyle(document.body).getPropertyValue('--cui-body-color').trim() || color;
    } catch {
      // keep fallback
    }
    const grid = 'rgba(128, 128, 128, 0.15)';
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: true, position: 'top', labels: { color } }
      },
      scales: {
        x: { ticks: { color }, grid: { color: grid } },
        y: { beginAtZero: true, ticks: { color }, grid: { color: grid } },
        y1: { beginAtZero: true, position: 'right', ticks: { color, precision: 0 }, grid: { drawOnChartArea: false } }
      }
    } as ChartOptions;
  }
}
